import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { WinnersOnlyNotice } from "@/components/WinnersOnlyNotice";
import { ShieldCheck, Lock } from "lucide-react";

const AGREE_KEY = "playden_age_agreed_v1";

/**
 * Full 5-point age-verification modal.
 *
 * Default behavior (no props): renders when the user has NOT previously
 * accepted (localStorage `playden_age_agreed_v1` is not "true"). This is
 * how the app-layout gate on `/home` etc. works.
 *
 * `forceOpen` (Compliance fix — Portal → Classic gate):
 *   When true, the modal always renders regardless of localStorage. Used by
 *   the Portal page to require a fresh 5-point acknowledgment before
 *   navigating a returning visitor into Playden Classic. The accept side
 *   still writes `playden_age_agreed_v1` so downstream gates stay quiet.
 *
 * `onAccept` / `onLeave` optional callbacks — fired in addition to the
 *   default localStorage / redirect behavior so the caller can chain UI
 *   actions (e.g. Portal unlocking the Classic card after acceptance).
 */
export function AgeGate({ forceOpen = false, onAccept, onLeave } = {}) {
  const [agreed, setAgreed] = useState(() => !forceOpen && localStorage.getItem(AGREE_KEY) === "true");
  const [declined, setDeclined] = useState(false);

  // Lock body scroll while the gate is up.
  useEffect(() => {
    if (!agreed) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [agreed]);

  if (agreed) return null;

  const accept = () => {
    localStorage.setItem(AGREE_KEY, "true");
    localStorage.setItem(AGREE_KEY + "_at", new Date().toISOString());
    setAgreed(true);
    // Let the How-To / Rules gate know it's their turn to appear.
    try { window.dispatchEvent(new Event("playden:gate-advance")); } catch (_) { /* noop */ }
    if (typeof onAccept === "function") { try { onAccept(); } catch (_) { /* noop */ } }
  };
  const leave = () => {
    setDeclined(true);
    if (typeof onLeave === "function") {
      try { onLeave(); } catch (_) { /* noop */ }
      return;
    }
    try { window.location.href = "https://www.google.com"; } catch (_) { /* noop */ }
  };

  return (
    <div
      data-testid="age-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      // Capture every interaction so nothing behind the gate is clickable.
      style={{ backgroundColor: "rgba(4, 6, 10, 0.86)", backdropFilter: "blur(8px)" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        className="w-full min-w-0 max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl border border-white/10 bg-[hsl(222_22%_8%)] shadow-[0_30px_90px_rgba(0,0,0,0.7)] overflow-hidden"
        data-testid="age-gate-dialog"
      >
        <div className="relative px-5 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">Age Verification</span>
                <span data-testid="age-gate-minimum-age" className="font-mono text-[9px] uppercase tracking-[0.2em] text-black bg-emerald-300 rounded-full px-2 py-0.5 whitespace-nowrap">21+ Only</span>
              </div>
              <h2 id="age-gate-title" data-testid="age-gate-title" className="font-display text-2xl text-white tracking-[0.05em] mt-1">YOU MUST AGREE TO ENTER.</h2>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-8 py-5 flex-1 min-h-0 overflow-y-auto overscroll-contain text-sm text-white/70 leading-relaxed space-y-3" data-testid="age-gate-terms">
          <WinnersOnlyNotice variant="inline" />
          <p>This site contains adult content. By selecting <span className="text-white font-medium">"I Agree"</span> below, you are entering a binding agreement and you affirm, represent, and warrant that:</p>
          <ul className="space-y-2 list-none">
            <li className="relative pl-6"><span className="absolute left-0 top-0 text-emerald-300 font-semibold">1.</span> You are <span className="text-white">at least 21 years of age</span> and of legal age to view adult material in your jurisdiction.</li>
            <li className="relative pl-6"><span className="absolute left-0 top-0 text-emerald-300 font-semibold">2.</span> You understand that all persons and cards depicted are <span className="text-white">21 or older</span> and that all depictions portray <span className="text-white">consensual activity between consenting adults</span>.</li>
            <li className="relative pl-6"><span className="absolute left-0 top-0 text-emerald-300 font-semibold">3.</span> You will <span className="text-white">engage only in lawful conduct</span> on this site and will <span className="text-white">never request, upload, or solicit any unlawful content</span> — including, without limitation, any material involving minors or non-consent.</li>
            <li className="relative pl-6"><span className="absolute left-0 top-0 text-emerald-300 font-semibold">4.</span> You accept <span className="text-white">sole responsibility and liability</span> for any prompt, request, upload, or content you submit, and you agree to <span className="text-white">indemnify and hold harmless</span> this site and its operators from any claim arising out of your conduct.</li>
            <li className="relative pl-6"><span className="absolute left-0 top-0 text-emerald-300 font-semibold">5.</span> Viewing this material is not unlawful where you live, and you are accessing it of your own free will.</li>
          </ul>
          <p className="text-white/45 text-[12px]">This acknowledgment is recorded as your consent. If you do not agree, or are under 21, you must leave now.</p>
        </div>

        <div data-testid="age-gate-actions" className="px-5 sm:px-8 py-4 border-t border-white/10 flex flex-col-reverse gap-3 shrink-0">
          <Button
            data-testid="age-gate-leave"
            variant="outline"
            onClick={leave}
            disabled={declined}
            className="w-full min-w-0 h-auto min-h-11 py-3 whitespace-normal border-white/15 text-white/70 hover:bg-white/5"
          >
            <Lock className="h-4 w-4 mr-1.5 shrink-0" /><span className="min-w-0">No — I'm under 21 / I don't agree</span>
          </Button>
          <Button
            data-testid="age-gate-agree"
            onClick={accept}
            className="w-full min-w-0 h-auto min-h-11 py-3 whitespace-normal bg-emerald-400 text-black hover:bg-emerald-300 font-medium"
          >
            <ShieldCheck className="h-4 w-4 mr-1.5 shrink-0" /><span className="min-w-0">Yes — I'm 21+ and I agree</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
